import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Banner,
  Button,
  Card,
  DOCKED_CARD_WIDTHS,
  EmptyPane,
  formatCalendarDate,
  Icon,
  Skeleton,
  useToast,
  type ToastId,
} from '@repo/design-system';
import type { AuthoredOpeningHoursDay, AuthoredOpeningHoursVersion } from '@repo/frontend-types';
import { space } from '@repo/design-tokens';
import { visuallyHidden } from '@repo/ui';

import type { LaunchHoursState } from '../useLaunchHours';
import type { OrganisationTwins } from '../useOrganisationTwins';
import { announcementFor, announcementKey, stepFooterFor } from '../launchHoursText';
import CalendarList from './CalendarList';
import { startProblem, type CopySource } from './CopyFromTwin';
import EditStandardHours, { EDIT_HOURS_GONE } from './EditStandardHours';
import NewStandardHours from './NewStandardHours';
import RemoveHours, { removeSentence } from './RemoveHours';
import SpecialPeriodCard from './SpecialPeriodCard';
import StartPanel from './StartPanel';
import WeekPanel, { commitWeek, type BulkKind } from './WeekPanel';
import {
  addDays,
  calendarKey,
  comparisonStandard,
  groupCalendars,
  groupOf,
  listGroups,
  newSpecialBody,
  sameDays,
  specialEnd,
  specialName,
  specialSpan,
  standardChangeSentence,
  standardChangedSentence,
  standardEditNote,
  standardName,
  standardNameStored,
  startPanelShows,
  type StandardFields,
  type StandardMove,
} from './hoursModel';
import {
  HOURS_STILL_SAVING,
  type HoursActionResult,
  type SpecialPeriodFields,
  type UseOpeningHoursEditor,
} from './useOpeningHoursEditor';

/**
 * **Opening hours** — the wizard's second step (spec twin-details-page-hours-week; Rob's frame 06).
 *
 * The twin's standard weeks by its own date, and the selected one's week, every change saved to the
 * twin straight away through the page-level editor (`useOpeningHoursEditor`). Its hours are the
 * twin's, never part of a version, so the page mounts it outside the version-swap remount, as Twin
 * details and Position on map.
 *
 * Before the week: the read's pending and failed states, a pointer to Twin details when the twin
 * has no timezone (hours are times on its own clock) or does not exist yet, and — when no standard
 * covers today or later — frame 05's start panel, whose quick starts set a whole week with one press
 * (spec twin-details-page-hours-start).
 *
 * Special periods (spec twin-details-page-hours-special): listed with the standards, their week
 * compared day by day with standard hours, and their name and dates in a card docked beside the week
 * — opened by *Add Special Period* or by selecting a running or upcoming one.
 */

export interface OnboardingOpeningHoursProps {
  editor: UseOpeningHoursEditor;
  /** The page's launch rule state — the step's footer and its announcements read it. */
  launchHours: LaunchHoursState;
  /** *Launch Twin* / *Update Twin*. */
  launchLabel: string;
  /** The twin's name, for the start panel's *When is {name} open?*; null when it has none. */
  twinName: string | null;
  /** The organisation's other twins, for *Copy another twin* (`useOrganisationTwins`). */
  organisationTwins: OrganisationTwins;
  onRetryOrganisationTwins: () => void;
  onGoToTwinDetails: () => void;
  className?: string;
}

const BULK_TITLE: Record<BulkKind, string> = {
  'copy-monday': 'Monday’s hours copied to Tuesday–Friday.',
  'all-24h': 'Every day set to 24 hours.',
  'same-as-standard': 'Every day set back to standard hours.',
};

const SPAN = (from: string, to: string) =>
  specialSpan({ effectiveDateFrom: from, effectiveDateTo: to });

const OnboardingOpeningHours: React.FC<OnboardingOpeningHoursProps> = ({
  editor,
  launchHours,
  launchLabel,
  twinName,
  organisationTwins,
  onRetryOrganisationTwins,
  onGoToTwinDetails,
  className,
}) => {
  const toast = useToast();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  /** The selection as of this render, for an answer that lands later (`onStandardChanged`). */
  const selectedKeyRef = useRef(selectedKey);
  selectedKeyRef.current = selectedKey;
  const [pastOpen, setPastOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const focusTitleRef = useRef(false);
  /** After *Try again*: focus the week's title once the hours are back, or *Try again* if not. */
  const retryingRef = useRef(false);
  const tryAgainRef = useRef<HTMLButtonElement>(null);
  /** After a Remove that leaves no calendar to show: focus the start panel's first quick start. */
  const focusEmptyRef = useRef(false);
  const firstStartRef = useRef<HTMLButtonElement>(null);
  /**
   * After *Copy another twin* across timezones: the copied calendar and its days, and the sentence
   * shown above it until the week next changes (R4.4.5).
   */
  const [zoneNote, setZoneNote] = useState<{
    key: string;
    days: AuthoredOpeningHoursDay[];
    text: string;
  } | null>(null);
  /** After a late outcome: if its popover went with focus in it, focus the week's title instead. */
  const rescueFocusRef = useRef(false);
  /**
   * *Edit details* (spec opening-hours-edit-standard-details, R3): its trigger, for focus after a
   * save (R3.6); the set its popover is open on; whether its change is on its way, so the step never
   * takes its own move for someone else's; and the sets that change moved, as worked out when sent.
   */
  const editTriggerRef = useRef<HTMLButtonElement>(null);
  const focusEditRef = useRef(false);
  const editingKeyRef = useRef<string | null>(null);
  const editSendingRef = useRef(false);
  const editMovesRef = useRef<readonly StandardMove[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);

  // ── The special period card (R6). Open for a new period while `adding`, else for the selected
  // running or upcoming special. A fresh session remounts it, so a draft never moves to another. ──
  const [adding, setAdding] = useState(false);
  const [cardSession, setCardSession] = useState(0);
  /*
   * The card session showing now, and whether its card has an add or change in flight. An answer
   * that lands after its card was closed or replaced is only said: it never re-selects or reopens.
   */
  const sessionRef = useRef(cardSession);
  sessionRef.current = cardSession;
  const cardSendingRef = useRef(false);
  const [focusName, setFocusName] = useState(false);
  /** The new period as typed: its week is shown read-only beside the card (R5.5). */
  const [draft, setDraft] = useState<{ name: string; from: string | null; to: string | null }>({
    name: '',
    from: null,
    to: null,
  });
  /** The calendar selected before the card opened, which closing it returns to (R6.6). */
  const returnKeyRef = useRef<string | null>(null);
  /** Where focus goes when the card goes with focus in it; pointed just before it closes. */
  const cardReturnFocus = useRef<HTMLElement | null>(null);
  const addSpecialRef = useRef<HTMLButtonElement>(null);

  // ── The step's one polite region (R9). A fresh node per sentence, so a repeat is spoken again. ──
  const [said, setSaid] = useState<{ n: number; text: string }>({ n: 0, text: '' });
  /**
   * What the step said since the last commit. A launch transition landing in the same commit — a
   * quick start that unblocks Launch — is added to it rather than said over it, so neither is lost.
   */
  const saidNow = useRef<string | null>(null);
  const announce = useCallback((text: string) => {
    saidNow.current = text;
    setSaid(s => ({ n: s.n + 1, text }));
  }, []);

  /** The notice this step raised itself — already announced, so the effect below skips it. */
  const localNotice = useRef<string | null>(null);

  // ── The Undo toasts after a bulk change, one per calendar (R7.8). ──
  const bulkToasts = useRef(new Map<string, { id: ToastId; after: AuthoredOpeningHoursDay[] }>());
  const dismissBulk = useCallback(
    (key?: string) => {
      bulkToasts.current.forEach((t, k) => {
        if (key === undefined || key === k) {
          toast.dismiss(t.id);
          bulkToasts.current.delete(k);
        }
      });
    },
    [toast],
  );
  // A toast must not outlive the step: its Undo closes over this twin's calendars.
  useEffect(() => () => dismissBulk(), [dismissBulk]);

  useEffect(() => {
    if (editor.notice && editor.notice !== localNotice.current) {
      announce(editor.notice);
      // The server said no, or someone else changed the hours: an Undo would now act on hours it
      // did not see.
      dismissBulk();
    }
    if (!editor.notice) localNotice.current = null;
  }, [editor.notice, announce, dismissBulk]);

  // The launch line's transitions, as Launch announces them — never on arrival.
  const launchKey = useRef(announcementKey(launchHours));
  useEffect(() => {
    const text = announcementFor(launchKey.current, launchHours, launchLabel);
    launchKey.current = announcementKey(launchHours);
    if (text) announce(saidNow.current ? `${saidNow.current} ${text}` : text);
  }, [launchHours, launchLabel, announce]);
  // Declared after every effect that announces: the commit is over.
  useEffect(() => {
    saidNow.current = null;
  });

  /**
   * The calendars as they are NOW, for a toast's Undo: it acts seconds after it was raised, by which
   * time the calendar's dates may have moved (new hours from a date end it), so it must never re-send
   * the calendar it closed over.
   */
  const versionsRef = useRef(editor.versions);
  versionsRef.current = editor.versions;

  /** Save a calendar's week; any other change to it withdraws its Undo. */
  const save = (
    calendar: AuthoredOpeningHoursVersion,
    days: AuthoredOpeningHoursDay[],
    fromUndo = false,
  ) => {
    if (!fromUndo) dismissBulk(calendarKey(calendar));
    editor.saveCalendar(calendar, days);
  };

  const refuse = (sentence: string) => {
    localNotice.current = sentence;
    editor.showNotice(sentence);
    announce(sentence);
  };

  /** An Add or Remove outcome its popover cannot show: closed, or gone with the week it was on. */
  const late = (sentence: string) => {
    rescueFocusRef.current = true;
    refuse(sentence);
  };

  /** `late` as of this render, for the effect below, which runs only when the sets change. */
  const lateRef = useRef(late);
  lateRef.current = late;

  const { read } = editor;

  useEffect(() => {
    if (focusEditRef.current) {
      // Once: with no *Edit details* to return to (a special is being added), the request lapses
      // rather than taking focus at some later render.
      focusEditRef.current = false;
      editTriggerRef.current?.focus();
    } else if (focusTitleRef.current && titleRef.current) {
      focusTitleRef.current = false;
      retryingRef.current = false;
      titleRef.current.focus();
    } else if (focusEmptyRef.current && firstStartRef.current) {
      focusEmptyRef.current = false;
      firstStartRef.current.focus();
    } else if (retryingRef.current && read.read === 'failed') {
      retryingRef.current = false;
      tryAgainRef.current?.focus();
    }
    if (rescueFocusRef.current) {
      rescueFocusRef.current = false;
      const active = document.activeElement;
      if (!active || active === document.body) {
        (titleRef.current ?? firstStartRef.current)?.focus();
      }
    }
  });

  /**
   * The set *Edit details* is open on went with someone else's change: its popover went with it, and
   * the step says so, once (spec opening-hours-edit-standard-details, Failure States). The popover's
   * own save is not someone else's, even when it moves the set to a new start: while it is sending,
   * this waits for its `onSending(false)`.
   */
  const editedSetGone = useCallback(() => {
    const key = editingKeyRef.current;
    if (!key || editSendingRef.current) return;
    if (versionsRef.current.some(v => calendarKey(v) === key)) return;
    editingKeyRef.current = null;
    lateRef.current(EDIT_HOURS_GONE);
  }, []);
  useEffect(() => editedSetGone(), [editor.versions, editedSetGone]);

  if (read.read === 'pending') {
    return (
      <div className={className} aria-busy="true">
        <Card surface="on-scene" size="lg" className="list-col">
          <Skeleton shape="block" />
        </Card>
        <div className="panel-col loading">
          {Array.from({ length: 7 }, (_, i) => (
            <Skeleton key={i} shape="block" />
          ))}
        </div>
      </div>
    );
  }

  // No twin yet (the create route), or one with no timezone: hours are times on the twin's own
  // clock, so the step points at Twin details. Neither is a failed read.
  const pointer = (
    <div className="panel-col wide">
      <EmptyPane
        icon={<Icon name="clock" size="lg" />}
        title="Opening hours need the twin’s timezone"
        actions={
          <Button variant="primary" onClick={onGoToTwinDetails}>
            Go to Twin details
          </Button>
        }
      >
        Hours are times on the twin’s own clock. Set the timezone on Twin details first.
      </EmptyPane>
    </div>
  );

  if (!editor.hasTwin) {
    return (
      <div className={className} ref={rootRef}>
        {pointer}
      </div>
    );
  }

  const announcer = (
    <div role="status" aria-live="polite" id="opening-hours-announcer" style={visuallyHidden}>
      <span key={said.n}>{said.text}</span>
    </div>
  );

  if (read.read === 'failed') {
    return (
      <div className={className} ref={rootRef}>
        {announcer}
        <div className="panel-col failed">
          <Banner
            tone="error"
            surface="on-glass"
            icon={<Icon name="circle-exclamation" />}
            title="Couldn’t read this twin’s opening hours."
          >
            {/* A write whose outcome is unknown fails its re-read too, landing here: say so. */}
            {editor.notice && <p className="failed-notice">{editor.notice}</p>}
            {editor.canRead && (
              <div className="failed-actions">
                <Button
                  ref={tryAgainRef}
                  variant="frosted"
                  size="sm"
                  onClick={() => {
                    retryingRef.current = true;
                    focusTitleRef.current = true;
                    editor.retryRead();
                  }}
                >
                  Try again
                </Button>
              </div>
            )}
          </Banner>
        </div>
      </div>
    );
  }

  const { hours } = read;
  const today = hours.launchHours.today;

  if (hours.launchHours.reason === 'no_timezone' || today === null) {
    return (
      <div className={className} ref={rootRef}>
        {announcer}
        {pointer}
      </div>
    );
  }

  const calendars = groupCalendars(editor.versions, today);
  const groups = listGroups(editor.versions, today);
  const fallback = calendars.now ?? calendars.upcoming[0] ?? null;
  const selected =
    (selectedKey && editor.versions.find(v => calendarKey(v) === selectedKey)) || fallback;
  const selectedGroup = selected ? groupOf(selected, today) : null;
  /** The saved period the card shows: a selected special that is running or upcoming. */
  const cardFor =
    !adding && selected && selected.kind === 'special' && selectedGroup !== 'past'
      ? selected
      : null;
  const cardOpen = adding || cardFor !== null;

  const listCard = (key: string) =>
    rootRef.current?.querySelector<HTMLElement>(`[data-calendar-key="${key}"]`) ?? null;

  /**
   * An unsaved new period goes; said only when anything was typed (R6.6). An add already sent is
   * left to say its own outcome when it lands.
   */
  const discardNew = () => {
    if (!cardSendingRef.current && (draft.name.trim() || draft.from || draft.to)) {
      announce('No special period added.');
    }
    setAdding(false);
  };

  /** The card goes, or moves to another period: its session ends. */
  const endSession = () => setCardSession(n => n + 1);

  const select = (key: string) => {
    const v = editor.versions.find(x => calendarKey(x) === key);
    const opens = !!v && v.kind === 'special' && groupOf(v, today) !== 'past';
    if (adding) discardNew();
    if (opens && !cardOpen) returnKeyRef.current = selected ? calendarKey(selected) : null;
    if ((opens || cardOpen) && (adding || key !== (cardFor && calendarKey(cardFor)))) {
      // Selecting a saved period does not move focus: the person is in the list (R8.2).
      setFocusName(false);
      endSession();
    }
    setSelectedKey(key);
  };

  const openAdd = () => {
    if (!cardOpen) returnKeyRef.current = selected ? calendarKey(selected) : null;
    setDraft({ name: '', from: null, to: null });
    setFocusName(true);
    setCardSession(n => n + 1);
    setAdding(true);
  };

  /** *Done*, the close button or Escape (R6.6). */
  const closeCard = () => {
    const closedKey = !adding && cardFor ? calendarKey(cardFor) : null;
    if (adding) discardNew();
    setFocusName(false);
    endSession();
    cardReturnFocus.current = (closedKey && listCard(closedKey)) || addSpecialRef.current;
    const back = returnKeyRef.current;
    returnKeyRef.current = null;
    const backThere = back !== null && editor.versions.some(v => calendarKey(v) === back);
    setSelectedKey(backThere ? back : calendars.now ? calendarKey(calendars.now) : null);
  };

  const onSpecialAdded = ({ name, from, to }: SpecialPeriodFields, session: number) => {
    if (session === sessionRef.current) {
      // The card stays, now on the saved period, with focus in it (R6.7).
      setAdding(false);
      setSelectedKey(calendarKey({ kind: 'special', effectiveDateFrom: from }));
    }
    announce(`${name} added, ${SPAN(from, to)}. Its week starts as a copy of standard hours.`);
  };

  const onSpecialChanged = (
    before: AuthoredOpeningHoursVersion,
    next: SpecialPeriodFields,
    session: number,
  ) => {
    if (session === sessionRef.current) {
      setSelectedKey(calendarKey({ kind: 'special', effectiveDateFrom: next.from }));
    }
    const said: string[] = [];
    if (next.name !== specialName(before))
      said.push(`${specialName(before)} renamed ${next.name}.`);
    if (next.from !== before.effectiveDateFrom || next.to !== specialEnd(before)) {
      said.push(`${next.name} now runs ${SPAN(next.from, next.to)}.`);
    }
    if (said.length > 0) announce(said.join(' '));
  };

  /** The week a new period starts with (R1.9), from its first day as typed, else today. */
  const previewOf = (d: typeof draft): AuthoredOpeningHoursVersion => {
    const from = d.from ?? today;
    const to = d.to ?? from;
    return {
      kind: 'special',
      name: null,
      effectiveDateFrom: from,
      effectiveDateTo: to,
      days: newSpecialBody(editor.versions, { name: '', from, to }).days,
    };
  };

  const onSpecialDeleted = (deleted: AuthoredOpeningHoursVersion, session: number) => {
    if (session === sessionRef.current) {
      const nowKey = calendars.now ? calendarKey(calendars.now) : null;
      cardReturnFocus.current = (nowKey && listCard(nowKey)) || addSpecialRef.current;
      returnKeyRef.current = null;
      setSelectedKey(nowKey);
    }
    announce(
      `${specialName(deleted)} deleted. Standard hours apply again on ${specialSpan(deleted)}.`,
    );
  };

  const onAdded = ({ from, name }: StandardFields) => {
    focusTitleRef.current = true;
    setSelectedKey(calendarKey({ kind: 'standard', effectiveDateFrom: from }));
    announce(
      `${standardName({ name: standardNameStored(name) })} from ${formatCalendarDate(from)} added.`,
    );
  };

  /**
   * *Edit details*' Save: what it moves is worked out once, from the sets as they are when it is
   * sent, for the editor's re-keying and the announcement alike. Like any other change to a set, it
   * withdraws the Undo of the sets it changes.
   */
  const changeStandard = (calendar: AuthoredOpeningHoursVersion, fields: StandardFields) => {
    const { moves } = standardEditNote(editor.versions, calendar, fields, today);
    editMovesRef.current = moves;
    dismissBulk(calendarKey(calendar));
    moves.forEach(m => dismissBulk(calendarKey(m.version)));
    return editor.changeStandard(calendar, fields, moves);
  };

  /**
   * Saved (R3.6): the selection follows the set, focus returns to its *Edit details*, and it is said.
   * Where the person selected another calendar while it saved, that selection stays, following its
   * set only when this save moved it.
   */
  const onStandardChanged = (
    before: AuthoredOpeningHoursVersion,
    fields: StandardFields,
    { focus }: { focus: boolean },
  ) => {
    editingKeyRef.current = null;
    const current = selectedKeyRef.current;
    const moved = editMovesRef.current.find(m => calendarKey(m.version) === current);
    if (current === calendarKey(before)) {
      setSelectedKey(calendarKey({ kind: 'standard', effectiveDateFrom: fields.from }));
    } else if (moved) {
      setSelectedKey(calendarKey({ kind: 'standard', effectiveDateFrom: moved.from }));
    }
    if (focus) focusEditRef.current = true;
    announce(standardChangedSentence(before, fields, editMovesRef.current));
  };

  const editSlot =
    !adding &&
    selected &&
    selected.kind === 'standard' &&
    (selectedGroup === 'now' || selectedGroup === 'upcoming') ? (
      <EditStandardHours
        key={calendarKey(selected)}
        calendar={selected}
        versions={editor.versions}
        today={today}
        busy={editor.busy}
        onChange={changeStandard}
        onChanged={onStandardChanged}
        // Where its set went too, `editedSetGone` says so in the same update, over this sentence.
        onLateProblem={late}
        onOpenChange={isOpen => {
          editingKeyRef.current = isOpen ? calendarKey(selected) : null;
        }}
        onSending={sending => {
          editSendingRef.current = sending;
          // A save whose answer took the set away, read before this render: said here, once.
          if (!sending) editedSetGone();
        }}
        triggerRef={editTriggerRef}
      />
    ) : null;

  /** A quick start or a copy landed: show the new week, focus its title and say so (R3.3). */
  const started = (sentence: string) => {
    focusTitleRef.current = true;
    setSelectedKey(calendarKey({ kind: 'standard', effectiveDateFrom: today }));
    announce(sentence);
  };

  /** A quick start or copy that did not land, unless it only waited for a save (the card says so). */
  const notStarted = (result: HoursActionResult) =>
    !(result.status === 'refused' && result.message === HOURS_STILL_SAVING);

  const onStart = async (title: string, days: AuthoredOpeningHoursDay[]) => {
    const result = await editor.startWith(days);
    if (result.status === 'saved') {
      started(`Standard hours from today set: ${title}. Adjust any day below.`);
    } else if (notStarted(result)) {
      const problem = startProblem(result);
      if (problem) refuse(problem);
    }
    return result;
  };

  // The copy popover shows its own failures (R4.4.6); only success is the step's.
  const onCopy = async ({ name, days, zoneNote: zone }: CopySource) => {
    const result = await editor.startWith(days);
    if (result.status === 'saved') {
      setZoneNote(
        zone
          ? { key: calendarKey({ kind: 'standard', effectiveDateFrom: today }), days, text: zone }
          : null,
      );
      started(`${name}’s standard week copied. Adjust any day below.${zone ? ` ${zone}` : ''}`);
    }
    return result;
  };

  const newHours = (label: string, variant: 'ghost' | 'primary') => (
    <NewStandardHours
      versions={editor.versions}
      today={today}
      busy={editor.busy}
      onAdd={editor.addFromDate}
      onAdded={onAdded}
      onLateProblem={late}
      label={label}
      variant={variant}
    />
  );

  const removeSlot =
    !adding && selected && editor.canRemove(selected) ? (
      <RemoveHours
        calendar={selected}
        versions={editor.versions}
        busy={editor.busy}
        onRemove={() => editor.removeUpcoming(selected)}
        onLateProblem={late}
        onRemoved={after => {
          announce(removeSentence(selected, editor.versions, 'done', after));
          // The week that took the dates back (R7.6), else the step's default.
          const dayBefore = addDays(selected.effectiveDateFrom, -1);
          const previous = editor.versions.find(
            v => v.kind === 'standard' && v.effectiveDateTo === dayBefore,
          );
          setSelectedKey(previous ? calendarKey(previous) : null);
          const left = after ? groupCalendars(after, today) : null;
          const panelLeft = previous || !left || left.now || left.upcoming.length > 0;
          focusTitleRef.current = !!panelLeft;
          focusEmptyRef.current = !panelLeft;
        }}
      />
    ) : null;

  const launchLine = stepFooterFor(launchHours, launchLabel);
  // Frame 05 (R1): no standard covers today or later, and none is chosen to look at.
  const starting = !selected && startPanelShows(editor.versions, today);
  // Any stored calendar keeps the list beside the start panel: a special alone must stay reachable.
  const hasCalendars = editor.versions.length > 0;
  const info =
    selected &&
    zoneNote &&
    zoneNote.key === calendarKey(selected) &&
    sameDays(selected.days, zoneNote.days)
      ? zoneNote.text
      : null;

  if (starting) {
    return (
      <div className={className} ref={rootRef}>
        {announcer}
        {hasCalendars && (
          // Past weeks or specials only: the list stays, without its foot — the start panel carries
          // the same action as *Start on a later date instead*, and it never appears twice (R1.2).
          <Card surface="on-scene" size="lg" className="list-col">
            <CalendarList
              calendars={groups}
              selectedKey={null}
              onSelect={select}
              pastOpen={pastOpen}
              onTogglePast={() => setPastOpen(o => !o)}
            />
          </Card>
        )}
        <StartPanel
          className={hasCalendars ? 'panel-col' : 'panel-col wide'}
          twinName={twinName}
          timezone={'timezone' in hours ? hours.timezone : undefined}
          launchHours={launchHours}
          launchLabel={launchLabel}
          notice={editor.notice}
          organisationTwins={organisationTwins}
          onRetryOrganisationTwins={onRetryOrganisationTwins}
          readTwinHours={editor.readTwinHours}
          onStart={onStart}
          onCopy={onCopy}
          onLateProblem={late}
          laterDate={newHours('Start on a later date instead', 'ghost')}
          firstCardRef={firstStartRef}
        />
      </div>
    );
  }

  return (
    <div className={cardOpen ? `${className ?? ''} with-card` : className} ref={rootRef}>
      {announcer}
      <Card surface="on-scene" size="lg" className="list-col">
        <CalendarList
          calendars={groups}
          selectedKey={!adding && selected ? calendarKey(selected) : null}
          onSelect={select}
          pastOpen={pastOpen}
          onTogglePast={() => setPastOpen(o => !o)}
          editSlot={editSlot}
          foot={
            <>
              <Button
                ref={addSpecialRef}
                variant="secondary"
                size="sm"
                iconLeft={<Icon name="calendar-plus" />}
                onClick={openAdd}
              >
                Add Special Period
              </Button>
              {newHours('New Standard Hours From a Date', 'ghost')}
            </>
          }
        />
      </Card>
      {adding ? (
        <WeekPanel
          key="special-new"
          className="panel-col"
          calendar={previewOf(draft)}
          group="upcoming"
          preview
          besideCard
          launchLine={null}
          notice={null}
          revision={editor.revision}
          onSave={() => undefined}
          onRefused={refuse}
          onBulk={() => undefined}
          titleRef={titleRef}
        />
      ) : selected ? (
        <WeekPanel
          key={calendarKey(selected)}
          className="panel-col"
          calendar={selected}
          group={groupOf(selected, today)}
          besideCard={cardOpen}
          comparison={
            selected.kind === 'special' ? comparisonStandard(editor.versions, selected) : null
          }
          changeLine={
            selected.kind === 'special' ? standardChangeSentence(editor.versions, selected) : null
          }
          timezone={hours.timezone}
          launchLine={launchLine}
          notice={editor.notice}
          info={info}
          revision={editor.revision}
          onSave={days => save(selected, days)}
          onRefused={refuse}
          onBulk={(kind, before, after) => {
            const key = calendarKey(selected);
            dismissBulk(key);
            const id = toast.show({
              tone: 'success',
              icon: <Icon name="check" />,
              title: BULK_TITLE[kind],
              inlineAction: {
                label: 'Undo',
                onAction: () => {
                  bulkToasts.current.delete(key);
                  const latest = versionsRef.current.find(v => calendarKey(v) === key);
                  // Only while the calendar still holds what the bulk change made (R7.8): gone, or
                  // changed since — by anyone — and there is nothing for this Undo to undo.
                  if (!latest || !sameDays(latest.days, after)) return;
                  commitWeek(before, days => save(latest, days, true), refuse);
                },
              },
            });
            bulkToasts.current.set(key, { id, after });
          }}
          removeSlot={removeSlot}
          titleRef={titleRef}
        />
      ) : null}
      {cardOpen && (
        <SpecialPeriodCard
          key={cardSession}
          calendar={cardFor}
          group={cardFor ? selectedGroup : null}
          versions={editor.versions}
          today={today}
          onAdd={editor.addSpecial}
          onAdded={fields => onSpecialAdded(fields, cardSession)}
          onChange={editor.changeSpecial}
          onChanged={(before, fields) => onSpecialChanged(before, fields, cardSession)}
          onSending={sending => {
            cardSendingRef.current = sending;
          }}
          canDelete={!!cardFor && editor.canDeleteSpecial(cardFor)}
          onDelete={() =>
            cardFor ? editor.removeSpecial(cardFor) : Promise.resolve({ status: 'failed' as const })
          }
          onDeleted={() => cardFor && onSpecialDeleted(cardFor, cardSession)}
          onDraft={setDraft}
          onSay={announce}
          onLateProblem={late}
          onClose={closeCard}
          returnFocusTo={cardReturnFocus}
          focusName={focusName}
        />
      )}
    </div>
  );
};

export default styled(OnboardingOpeningHours)`
  display: grid;
  /* Frame 06: the calendars list in a 300 px column (measured), the week panel the rest — layout geometry. */
  grid-template-columns: 300px minmax(0, 1fr);
  gap: ${space[4]};
  align-items: stretch;
  min-height: 100%;
  padding: ${space[4]};

  /* The special period card's column, beside the week (R6.8). */
  &.with-card {
    grid-template-columns: 300px minmax(0, 1fr) ${DOCKED_CARD_WIDTHS.narrow};
  }

  /* The calendars list: the design system's glass card (Card on-scene, lg, whose radius is
     radius.xl), at frame 06's 12 px padding, which this rule's two classes hold over the Card's. */
  .list-col {
    display: flex;
    flex-direction: column;
    padding: ${space[3]};
  }

  .panel-col.loading {
    display: flex;
    flex-direction: column;
    gap: ${space[2]};
    padding: ${space[4]};
  }

  .panel-col.wide {
    grid-column: 1 / -1;
  }

  .panel-col.failed {
    grid-column: 1 / -1;
    padding: ${space[4]};
  }

  .failed-notice {
    margin: 0;
  }

  .failed-actions {
    margin-top: ${space[2]};
  }
`;
